<template>
  <v-container fluid pa-0>
    <v-row row wrap>
      <v-col cols="12">
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :item-class="itemRowBackground"
        >
          <template #[`item.handling`]="{item}">
            <v-chip outlined v-if="itemStatus(item).text" x-small :dark="itemStatus(item).dark" :color="itemStatus(item).color">
              {{ itemStatus(item).text }}
            </v-chip>
          </template>
          <template #[`item.unitPrice`]="{item}">
            {{item.unitPrice | currency(currencySymbol)}}
          </template>
          <template #[`item.discountValue`]="{item}">
            {{item.discountValue | currency(currencySymbol)}}
          </template>
          <template #[`item.tax`]="{item}">
            {{item.tax | currency(currencySymbol)}}
          </template>
          <template #[`item.price`]="{item}">
            {{item.price | currency(currencySymbol)}}
          </template>
          <template #[`item.product.name`]="{item}">
            {{item.product ? item.product.name : item.custom_label}}
          </template>
          <template #[`item.actions`]="{item}">
            <v-menu offset-y bottom left v-if="!readOnly">
              <template #activator="{ on }">
                <v-btn icon v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item @click="remove(item)" :disabled="item.handling > 0 || item.already_picked > 0">
                  <v-list-item-title>Remove</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="$can('discount', 'Order') && item.discountAmount == null" @click="discount(item)" :disabled="item.handling > 0">
                  <v-list-item-title>Discount</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="$can('discount', 'Order') && item.discountAmount > 0" @click="doRemoveDiscount(item)" :disabled="item.handling > 0">
                  <v-list-item-title>Remove Discount</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
        <order-row-discount
          ref="discountForm"
          v-on:updated="updateDiscount()"
        ></order-row-discount>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import hasQueryParams from '../../mixins/hasQueryParams.vue';
import OrderRowDiscount from "./OrderRowDiscount.vue";

export default {
  name: 'OrderRowsList',
  mixins: [hasQueryParams],
  components: {
    OrderRowDiscount
  },
  props: {
    items: {
      type: Array
    },
    currencySymbol: {
      type: String
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    headers: {
      type: Array,
      default: () => null
    },
    order: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      search: "",
      options: { itemsPerPage: 20 },
      formOpen: false,
      discountItem: null,
      discountForm: false,
      queryFields: ['page', 'itemsPerPage', 'sortBy', 'sortDesc']
    };
  },
  watch: {
    options () {
      this.pushNewRoute()
    }
  },
  mounted() {
    this.parseQuery()
    if (this.readOnly) {
      this.headers.pop();
    }
  },
  computed:{
    ...mapGetters("auth", ["currentUser"]),
    hasDiscount () {
      return this.order.discounts.length || this.items.filter(item => item.discountAmount > 0).length
    },
    tableHeaders() {
      if(this.headers) {
        return this.headers
      }

      const headers = [
        { text: "Status", value: "handling", align: "left" },
        { text: "Type", value: "typeId", align: "left" },
        { text: "Description", value: "product.name", align: "left" },
        { text: "Qty", value: "quantity", align: "center" },
        {
          text: "Unit Price",
          value: "unitPrice",
          align: "right",
          class: "nowrap"
        },
        { text: "Tax", value: "tax", align: "right", class: "nowrap" },
        { text: "Price", value: "price", align: "right", class: "nowrap" },
        { text: "Actions", value: "actions" }
      ]

      if(this.hasDiscount){
        headers.splice(5,0,{
            text: "Discount",
            value: "discountValue",
            align: "right",
            class: "nowrap"
          },
          { text: "Discount Label", value: "discountLabel", align: "left" },
        )
      }


      return headers

    },
    query () {
      return {
        page: this.options.page.toString(),
        itemsPerPage: this.options.itemsPerPage.toString(),
        sortBy: this.options.sortBy.toString(),
        sortDesc: this.options.sortDesc.toString()
      }
    }
  },
  methods: {
    ...mapActions('orders', ['removeProduct']),
    ...mapActions('orderRows', ['removeDiscount']),
    remove(orderRow) {
      this.loading = true;
      this.removeProduct({
          row: orderRow
        })
        .then(({order}) => {
          if (order.id) {
            this.$emit("removeProduct");
            this.flashMessage.success({
              message: "Delete row successfully",
            });
          } else {
            this.message = order.message;
          }
        })
        .finally(() => (this.loading = false));
    },
    discount(item) {
      this.$refs.discountForm.open(item)
    },
    doRemoveDiscount(item) {
      this.removeDiscount(item)
      this.updateDiscount()
    },
    updateDiscount() {
      this.$emit("update");
    },
    parseQuery(){
      this.options = {
        ...this.options,
        page: this.$route.query.page ? parseInt(this.$route.query.page): 1,
        itemsPerPage: this.$route.query.itemsPerPage ? parseInt(this.$route.query.itemsPerPage): 15,
        sortBy: [this.$route.query.sortBy ?? "typeId" ],
        sortDesc: [this.$route.query.sortDesc ?? true]
      }
    },
    itemRowBackground(item) {
      return item.handling > 0 ? 'handling-item' : ''
    },
    itemStatus(item) {
      if(item.invoiced >= item.quantity) {
        return {
          color: 'green',
          dark: true,
          text: 'invoiced'
        }
      }
      if(item.invoiced > 0) {
        return {
          color: 'green lighten-3',
          dark: false,
          text: 'partially invoiced'
        }
      }
      if(item.handling > 0) {
        return {
          color: 'red',
          dark: true,
          text: 'in build'
        }
      }
      if(item.already_picked > 0){
        return {
          color: 'blue',
          dark: true,
          text: item.already_picked < item.quantity ? 'partially picked' : 'picked'
        }
      }

      return {
          color: 'white',
          dark: true,
          text: null
        }
    }
  }
};
</script>

<style lang="less">
.nowrap {
  white-space: nowrap;
}
.handling-item td{
  background-color: #ECEFF1
}
</style>
